import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { gql, useClient } from '@web/api/src';
import { ModalWrapper, PortalModal } from '@web/utils/src';
import cartIconDark from '@web/utils/src/assets/icons/functions/cart-icon-dark.svg';
import plusIcon from '@web/utils/src/assets/icons/functions/plus-icon.svg';
import { useCustomToast } from '@/hooks/useCustomToast';
import { useAppSelector } from '@/store/hooks';
import { ArticleData } from '@agrantis/cart';

export interface AddToCartButtonProps {
  handleAddToCartPress: (article: ArticleData) => void;
  article: ArticleData;
}

const checkTotalQuery = gql`
  query getTotalLineItemsbyInventory($inventoryId: ID!) {
    inventory(id: $inventoryId) {
      orderLineItemsConnection(after: "0") {
        pageInfo {
          total
        }
      }
    }
  }
`;

const AddToCartButton = (props: AddToCartButtonProps) => {
  const [warningModalOpen, setWarningModalOpen] = useState(false);
  const settings = useAppSelector((state) => state.retailer.retailer?.settings);
  const isWarningEnabled = !!settings?.reorderArticles;
  const { t } = useTranslation();
  const client = useClient();

  const { handleAddToCartPress, article } = props;
  const customToast = useCustomToast();

  const handleClick = async () => {
    if (isWarningEnabled) {
      try {
        const result = await client.query(checkTotalQuery, {
          inventoryId: article.item.id,
        });
        const totalLineItems =
          result?.data?.inventory?.orderLineItemsConnection?.pageInfo?.total ??
          0;

        if (totalLineItems) {
          handleAddToCartPress(article);
        } else {
          setWarningModalOpen(true);
        }
      } catch {
        customToast('error', t('global.something_went_wrong'));
      }
    } else {
      handleAddToCartPress(article);
    }
  };

  const reorderWarningText = t('inventory.reorder_warning', {
    articleName: article.name,
  });
  const [beforeArticle, afterArticle] = reorderWarningText.split(article.name);

  const handleSubmitWarningModal = () => {
    handleAddToCartPress(article);
    setWarningModalOpen(false);
  };

  return (
    <>
      {warningModalOpen && (
        <PortalModal
          isOpen={warningModalOpen}
          setIsOpen={setWarningModalOpen}
          data-testID="warning-modal"
        >
          <ModalWrapper
            setIsOpen={setWarningModalOpen}
            submitText={t('inventory.warning_accept')}
            cancelText={t('inventory.warning_decline')}
            t={t}
            submitHandler={handleSubmitWarningModal}
          >
            <p className="py-2-0">
              {beforeArticle}
              <strong>{article.name}</strong>
              {afterArticle}
            </p>
          </ModalWrapper>
        </PortalModal>
      )}
      <div
        className="bg-primary-yellow rounded-base p-2-0 md:p-0-8 flex items-center gap-0-5 md:gap-1-0 cursor-pointer"
        onClick={handleClick}
      >
        <img src={cartIconDark} alt="Cart" data-testid={`cart-${article.id}`} />
        <img src={plusIcon} alt="Plus" data-testid={`plus-${article.id}`} />
      </div>
    </>
  );
};

export default AddToCartButton;
